import { useEffect } from "react";
import { XIcon, SparklesIcon } from "../../../ui/icons";
import { APP_RELEASES, APP_VERSION } from "../../../shared/version";

interface ReleaseNotesModalProps {
  readonly onClose: () => void;
}

function formatDate(date: string): string {
  const [year, month, day] = date.split("-");
  const months = ["jan.", "fév.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
  return `${Number(day)} ${months[Number(month) - 1]} ${year}`;
}

export function ReleaseNotesModal({ onClose }: ReleaseNotesModalProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-[60] animate-fade-in backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-md bg-surface rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up max-h-[75vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-accent" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-ink">Nouveautés</h2>
                <p className="text-[10px] text-ink-faint">Historique des mises à jour</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-alt text-ink-muted transition-colors"
              aria-label="Fermer"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto scrollbar-custom">
            {APP_RELEASES.length === 0 ? (
              <p className="text-xs text-ink-muted text-center py-10">
                Aucune note de version disponible.
              </p>
            ) : (
              <div className="flex flex-col">
                {APP_RELEASES.map((release, idx) => {
                  const isCurrent = release.version === APP_VERSION;
                  const isLatest = idx === 0;

                  return (
                    <div
                      key={release.version}
                      className={`px-6 py-4 ${idx < APP_RELEASES.length - 1 ? "border-b border-border/40" : ""} ${isLatest ? "bg-accent/5" : ""}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                          isLatest
                            ? "bg-accent text-white"
                            : "bg-surface-alt text-ink"
                        }`}>
                          v{release.version}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] font-medium text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                            actuelle
                          </span>
                        )}
                        <span className="text-[10px] text-ink-faint ml-auto">
                          {formatDate(release.date)}
                        </span>
                      </div>
                      <ul className="flex flex-col gap-1.5">
                        {release.changes.map((change, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-ink-muted leading-relaxed">
                            <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-accent/40" />
                            {change}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <footer className="px-6 py-3 border-t border-border shrink-0 bg-surface">
            <p className="text-[10px] text-ink-faint text-center">
              Palnia v{APP_VERSION}
            </p>
          </footer>
        </div>
      </div>
    </>
  );
}
